import { LitElement, html } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import { effect } from "@preact/signals-core";
import { getCardSignals, BadgeResult } from "../store/collection-store";

/**
 * Reactive discount badge for collection cards.
 *
 * Replaces the static `{% render 'discount-badge-product-card' %}` /
 * `{% render 'discount-badge-variant-card' %}` snippets.
 *
 * Attributes:
 *   data-card-id          — matches the card registered in collection-store
 *   data-text-exclusive   — translated "Exclusive Offer" label (from Liquid `t` filter)
 *   data-text-special     — translated "Special Offer" label (from Liquid `t` filter)
 */
@customElement("card-badge")
export class CardBadge extends LitElement {
  @property({ type: String, attribute: "data-card-id" }) cardId = "";
  @property({ type: String, attribute: "data-text-exclusive" }) textExclusive = "Exclusive Offer";
  @property({ type: String, attribute: "data-text-special" }) textSpecial = "Special Offer";

  @state() private _badge: BadgeResult = { type: "exclusive-offer", value: null };

  private _disposeEffect?: () => void;

  protected createRenderRoot(): HTMLElement | DocumentFragment {
    return this;
  }

  connectedCallback() {
    super.connectedCallback();
    const signals = getCardSignals(this.cardId);
    if (!signals) {
      console.error(`card-badge: no signals for card "${this.cardId}"`);
      return;
    }
    this._disposeEffect = effect(() => {
      this._badge = signals.badge.value;
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this._disposeEffect?.();
  }

  render() {
    const { type, value } = this._badge;

    if (type === "exclusive-offer") {
      return html`
        <div class="discount-badge exclusive">
          <div class="discount-badge__text exclusive">${this.textExclusive}</div>
        </div>
      `;
    }

    if (type === "special-offer") {
      return html`
        <div class="discount-badge special">
          <div class="discount-badge__text special">${this.textSpecial}</div>
        </div>
      `;
    }

    // regular — show percentage
    return html`
      <div class="discount-badge">
        <div class="discount-badge__text">${value}</div>
        <div class="discount-badge__symbol">
          <span class="symbol-percentage">%</span>
          <span class="symbol-text">OFF</span>
        </div>
      </div>
    `;
  }
}
